import i18n from 'i18next';
import type { BackendModule, ReadCallback } from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { enResource, loadLanguageResource, supportedLanguages } from './locales';
import { navByLang, toolsByLang } from './locales/newLandingNav';

// Lazy backend: English is bundled up front (see `resources` below); every
// other language is fetched as its own chunk the first time it is requested.
// This keeps ~1MB of translation data out of the initial download.
const lazyBackend: BackendModule = {
  type: 'backend',
  init: () => {},
  read: (language: string, _namespace: string, callback: ReadCallback) => {
    loadLanguageResource(language)
      .then((resource) => callback(null, resource ?? false))
      .catch((error) => callback(error as Error, false));
  },
};

i18n
  // Load non-bundled languages on demand
  .use(lazyBackend)
  // Detect user language
  .use(LanguageDetector)
  // Pass the i18n instance to react-i18next
  .use(initReactI18next)
  // Init i18next
  .init({
    // Only English is bundled synchronously; other languages come from
    // lazyBackend. partialBundledLanguages tells i18next to use the backend
    // even though some resources are provided inline.
    resources: { en: { translation: enResource } },
    partialBundledLanguages: true,
    fallbackLng: 'en',
    supportedLngs: supportedLanguages,
    debug: false,

    interpolation: {
      escapeValue: false, // React already escapes values
    },

    detection: {
      // Order of detection
      order: ['path', 'localStorage', 'navigator'],

      // Keys to lookup language from
      lookupLocalStorage: 'i18nextLng',
      lookupFromPathIndex: 0,

      // Cache user language
      caches: ['localStorage'],
    },

    // Allow "en-US" to fall back to "en"
    load: 'languageOnly',

    // Non-English translations load asynchronously. Render immediately with the
    // English fallback instead of suspending the whole tree while a language
    // chunk downloads.
    react: {
      useSuspense: false,
    },
  });

// The site-wide GlobalNav (rendered by Layout on every page, including first
// paint) needs its strings synchronously in EVERY language, so the small
// nav/tools bundle is registered up front for all languages here. The heavy
// page namespaces (newLanding, lyricsVideo) are registered lazily by the
// chunks that use them — see locales/registerNewLandingI18n.ts.
for (const code of supportedLanguages) {
  i18n.addResourceBundle(
    code,
    'translation',
    {
      globalNav: {
        nav: navByLang[code] ?? navByLang.en,
        tools: toolsByLang[code] ?? toolsByLang.en,
      },
    },
    true,
    true,
  );
}

export default i18n;
